import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ClipboardList, Calculator, Plus, Trash2 } from 'lucide-react';

const SkogsprojektKalkylator = () => {
  const [areal, setAreal] = useState(10);
  const [planteringskostnad, setPlanteringskostnad] = useState(15000);
  const [rojningskostnad, setRojningskostnad] = useState(3500);
  const [gallringKostnad, setGallringKostnad] = useState(0);
  const [extraKostnader, setExtraKostnader] = useState([]);
  const [result, setResult] = useState(null);

  const addExtraKostnad = () => {
    setExtraKostnader([...extraKostnader, { namn: '', kostnadPerHa: 0 }]);
  };

  const updateExtraKostnad = (index, field, value) => {
    const updated = [...extraKostnader];
    updated[index][field] = field === 'kostnadPerHa' ? Number(value) : value;
    setExtraKostnader(updated);
  };

  const removeExtraKostnad = (index) => {
    setExtraKostnader(extraKostnader.filter((_, i) => i !== index));
  };

  const calculateProject = () => {
    const planteringTotal = areal * planteringskostnad;
    const rojningTotal = areal * rojningskostnad;
    const gallringTotal = areal * gallringKostnad;
    
    const extraTotal = extraKostnader.reduce((sum, k) => sum + (k.kostnadPerHa * areal), 0);
    
    const totalKostnad = planteringTotal + rojningTotal + gallringTotal + extraTotal;
    
    const kostnadsDetaljer = [
      { namn: 'Plantering', perHa: planteringskostnad, total: planteringTotal },
      { namn: 'Röjning', perHa: rojningskostnad, total: rojningTotal },
      { namn: 'Gallring', perHa: gallringKostnad, total: gallringTotal },
      ...extraKostnader.map(k => ({ 
        namn: k.namn || 'Övrig kostnad', 
        perHa: k.kostnadPerHa, 
        total: k.kostnadPerHa * areal 
      }))
    ].filter(k => k.total > 0);
    
    setResult({
      kostnadsDetaljer,
      totalKostnad,
      kostnadPerHa: Math.round(totalKostnad / areal)
    });
  };

  return (
    <Card className="w-full" data-testid="skogsprojekt-calculator">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-forest-700">
          <ClipboardList className="h-5 w-5" />
          Skogsprojekt kostnadskalkyl
        </CardTitle>
        <CardDescription>
          Beräkna totalkostnaden för ett skogsprojekt. Summera plantering, röjning, 
          gallring och andra skogsvårdskostnader för att få en projektbudget.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="areal" className="font-medium">Projektareal (hektar)</Label>
          <Input
            id="areal"
            type="number"
            step="0.1"
            value={areal}
            onChange={(e) => setAreal(Number(e.target.value))}
            min={0.1}
            className="max-w-xs"
            data-testid="areal-input"
          />
        </div>

        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-forest-800">Kostnader per hektar</h4>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="plantering">Plantering (kr/ha)</Label>
              <Input
                id="plantering"
                type="number"
                value={planteringskostnad}
                onChange={(e) => setPlanteringskostnad(Number(e.target.value))}
                min={0}
                data-testid="plantering-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="rojning">Röjning (kr/ha)</Label>
              <Input
                id="rojning"
                type="number"
                value={rojningskostnad}
                onChange={(e) => setRojningskostnad(Number(e.target.value))}
                min={0}
                data-testid="rojning-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="gallring">Gallring (kr/ha)</Label>
              <Input
                id="gallring"
                type="number"
                value={gallringKostnad}
                onChange={(e) => setGallringKostnad(Number(e.target.value))}
                min={0}
                data-testid="gallring-input"
              />
            </div>
          </div>
          
          {/* Extra costs */}
          {extraKostnader.length > 0 && (
            <div className="space-y-2 pt-2 border-t">
              <p className="text-sm font-medium text-forest-700">Övriga kostnader</p>
              {extraKostnader.map((kostnad, index) => (
                <div key={index} className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Input
                      placeholder="Beskrivning"
                      value={kostnad.namn}
                      onChange={(e) => updateExtraKostnad(index, 'namn', e.target.value)}
                    />
                  </div>
                  <div className="w-32">
                    <Input
                      type="number"
                      placeholder="kr/ha"
                      value={kostnad.kostnadPerHa}
                      onChange={(e) => updateExtraKostnad(index, 'kostnadPerHa', e.target.value)}
                    />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => removeExtraKostnad(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={addExtraKostnad}
            className="mt-2"
          >
            <Plus className="h-4 w-4 mr-1" />
            Lägg till kostnad
          </Button>
        </div>

        <Button 
          onClick={calculateProject} 
          className="w-full bg-forest-700 hover:bg-forest-800"
          data-testid="calculate-btn"
        >
          <Calculator className="mr-2 h-4 w-4" />
          Beräkna projektkostnad
        </Button>

        {result && (
          <div className="mt-4 p-4 bg-forest-50 rounded-lg border border-forest-200" data-testid="result">
            <h4 className="font-semibold text-forest-800 mb-3">Projektbudget för {areal} hektar</h4>
            
            <div className="space-y-2 pb-3 border-b border-forest-200">
              {result.kostnadsDetaljer.map((kostnad, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">{kostnad.namn}</span>
                  <div className="text-right">
                    <span className="text-sm text-muted-foreground mr-4">
                      {kostnad.perHa.toLocaleString('sv-SE')} kr/ha
                    </span>
                    <span className="font-medium text-forest-700">
                      {kostnad.total.toLocaleString('sv-SE')} kr
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pt-3 flex justify-between items-center">
              <div>
                <p className="text-sm text-muted-foreground">Total projektkostnad</p>
                <p className="text-2xl font-bold text-forest-700">
                  {result.totalKostnad.toLocaleString('sv-SE')} kr
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Genomsnitt</p>
                <p className="text-lg font-semibold text-forest-600">
                  {result.kostnadPerHa.toLocaleString('sv-SE')} kr/ha
                </p>
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-4">
          Använd denna kalkyl för att budgetera skogsvårdsprojekt. 
          Kostnader kan variera beroende på terräng, region och entreprenör.
        </p>
      </CardContent>
    </Card>
  );
};

export default SkogsprojektKalkylator;
